/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined, WarningOutlined } from '@ant-design/icons'
import { Alert, Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import React, { useCallback, useMemo } from 'react'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { isCanlan } from '@sportninja/common/utils/customer-name'

interface DeleteVideographerModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  videographerName: string
  isLoading: boolean
  error: string | null
  teamName?: string
}

const DeleteVideographerModal: React.FC<DeleteVideographerModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  videographerName,
  isLoading,
  error,
  teamName,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose()
    }
  }, [onClose, isLoading])

  const handleConfirm = useCallback(async () => {
    await onConfirm()
  }, [onConfirm])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.ERROR_200};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          Remove Videographer
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              Are You Sure You Want To Remove {videographerName}
              {teamName ? ` From ${teamName}` : ''}? They Will No Longer Be Able
              To Stream Video For This Team.
            </Text>
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              background: ${colors.ERROR_200};
              border-radius: 8px;
              border: 2px solid ${colors.ERROR_200};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.5 : 1};
              transition: all 0.2s;
              &:hover {
                opacity: ${isLoading ? 0.5 : 0.8};
              }
            `}
            onClick={!isLoading ? handleConfirm : undefined}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              {isLoading ? 'REMOVING...' : 'REMOVE'}
            </Text>
          </button>
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={handleClose}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              CANCEL
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteVideographerModal
